<template>
    <div>
        <form @submit.prevent class="m-form" name="claimInfoForm">
            <div class="flex-grid flex-grid--reduced-bottom-margin a-form-inputs">
                <div class="flex-grid__item">
                    <control for="claimant_date_of_onset" :label="getClaimantDetail('diseaseOrAccident') === 'disease' ? 'Claimant Date of Onset Symptoms' : 'Claimant Date of Injury / Accident'">
                        <date-input v-model="onsetOfSymptoms" name="claimant_date_of_onset"
                                    v-field-control:claimant_date_of_onset.required="{type:'date'}">
                        </date-input>
                        <control-error :show="$error('claimant_date_of_onset') || !onsetRules">
                            The claimant's onset of symptoms is required, must be in the format DD/MM/YYYY
                            and must be after the Claimant's date of birth {{ dateOfBirth }}
                        </control-error>
                    </control>
                    <control for="claimants_assumed_dod_type" label="Claimant's Assumed Date of Death">
                        <select name="claimants_assumed_dod_type"
                                id="claimants_assumed_dod_type"
                                @change="updateAssumedLifeExpectancy"
                                v-model="assumedLifeExpectancyMethod"
                                v-field-control
                                required>
                            <option :value="null" disabled hidden>Please select...</option>
                            <option value="assumed_years_and_months_left">Enter Years/Months</option>
                            <option value="assumed_date">Enter Date</option>
                            <option value="assumed_age_at_death">Enter Age at Death (years)</option>
                            <option value="assumed_percentage_of_life">% of Normal Life Expectancy</option>
                            <option value="assumed_reduction_in_life">% Reduction in Normal Life Expectancy</option>
                        </select>
                        <control-error :show="$error('claimants_assumed_dod_type')">
                            The claimant's assumed Date of Death is required
                        </control-error>
                    </control>
                    <control for="claimants_age_at_assumed_death" label="Claimant's Age at Assumed Death" 
                    v-if="assumedLifeExpectancyMethod === 'assumed_age_at_death'"
                             before-after="years-old">
                        <input autocomplete="off" v-field-control
                               required
                               :disabled="assumedLifeExpectancyMethod !== 'assumed_age_at_death'"
                               name="claimants_age_at_assumed_death"
                               v-model.number="ageAtDeath"
                               v-two-dp
                               @input="updateAssumedLifeExpectancy"
                               type="number">
                    </control>
                    <control for="claimants_assumed_years_left"
                             label="Enter Years/Months From Today"
                             v-if="assumedLifeExpectancyMethod === 'assumed_years_and_months_left'">
                        <div class="a-form-inputs--half">
                            <div class="a-form-inputs__container">
                                <select id="years" v-model.number="yearsLeft"
                                        @change="updateAssumedLifeExpectancy">
                                    <option v-for="year in 76" :value="year - 1">{{ year - 1}} year(s)</option>
                                </select>
                            </div>
                            <div class="a-form-inputs__container">
                                <select id="months" v-model.number="monthsLeft"
                                        @change="updateAssumedLifeExpectancy">
                                    <option v-for="month in 12" :value="month - 1">
                                        {{ month - 1 }} month(s)
                                    </option>
                                </select>
                            </div>
                        </div>
                    </control>
                    <control label="Percentage of Normal Life Expectancy" for="percentage_of_normal_life_expectancy"
                             v-if="assumedLifeExpectancyMethod == 'assumed_percentage_of_life'"
                             before-after="percent">
                        <input autocomplete="off"
                               v-model.number="innerAssumedLifeExpectancy.percentageOfNormalLifeExpectancy"
                               :disabled="assumedLifeExpectancyMethod != 'assumed_percentage_of_life'"
                               v-field-control
                               v-two-dp
                               required
                               max="100"
                               min="1"
                               @input="updateAssumedLifeExpectancy"
                               name="percentage_of_normal_life_expectancy"
                               type="number">
                        <control-error :show="$error('percentage_of_normal_life_expectancy')">
                            The percentage of normal life expectancy must be number between 1 and 100
                        </control-error>
                    </control>
                    <control label="Percentage Reduction in Normal Life Expectancy"
                             for="percentage_reduction_in_normal_life_expectancy"
                             v-if="assumedLifeExpectancyMethod == 'assumed_reduction_in_life'"
                             before-after="percent">
                        <input autocomplete="off"
                               v-model.number="innerAssumedLifeExpectancy.percentageReductionInNormalLifeExpectancy"
                               :disabled="assumedLifeExpectancyMethod != 'assumed_reduction_in_life'"
                               v-field-control
                               required
                               max="100"
                               min="1"
                               @input="updateAssumedLifeExpectancy"
                               name="percentage_reduction_in_normal_life_expectancy"
                               type="number">
                        <control-error :show="$error('percentage_reduction_in_normal_life_expectancy')">
                            The percentage reduction in normal life expectancy must be number between 1 and 100
                        </control-error>
                    </control>
                    <control for="claimants_assumed_date_of_death" label="Claimant's Assumed Date of Death">
                        <date-input v-model="dateOfDeath"
                                    v-field-control:claimants_assumed_date_of_death.required="{type:'date'}"
                                    @input="updateAssumedLifeExpectancy"
                                    :disabled="assumedLifeExpectancyMethod !== 'assumed_date'"
                        ></date-input>
                        <control-error :show="$error('claimants_assumed_date_of_death')">
                            The claimant's assumed date of death is required
                        </control-error>
                    </control>
                    <life-expectancy
                            label="Claimant's 'But For' Life Expectancy"
                            :person="claimant"
                            :date="theDate"
                            :assumed-settlement="getClaimantInfoValue('dateOfAssumedSettlement')"
                            type="deceased"
                            @update="updateLife"
                            :stepValid="isValid()"
                    ></life-expectancy>
                </div>
                <div class="flex-grid__item flex-grid--space-left">
                    <control for="reduction_in_claimants_life_expectancy"
                             label="Reduction in Claimant's Life Expectancy" before-after="years">
                        <input autocomplete="off" v-model.number="claimantReductionInLife"
                               required
                               v-two-dp
                               disabled
                               name="reduction_in_claimants_life_expectancy"
                               type="number">
                        <control-error :show="!claimantReductionInLifeRules">
                            Reduction in Claimant's Life Expectancy must be greater than 0
                        </control-error>
                    </control>
                    <control for="claimants_age_at_assumed_death" label="Claimant's Age at Assumed Death"
                            v-if="assumedLifeExpectancyMethod !== 'assumed_age_at_death'"
                             before-after="years-old">
                        <input autocomplete="off" v-field-control
                               required
                               :disabled="assumedLifeExpectancyMethod !== 'assumed_age_at_death'"
                               name="claimants_age_at_assumed_death"
                               v-model.number="ageAtDeath"
                               v-two-dp
                               @input="updateAssumedLifeExpectancy"
                               type="number">
                    </control>
                    <control for="period_between_onset_of_symptoms_and_assumed_dod"
                             label="Period Between Onset of Symptoms and Assumed DOD" before-after="years">
                        <input autocomplete="off" v-model.number="periodBetweenOnsetAndAssumedDod"
                               v-field-control
                               required
                               disabled
                               v-two-dp
                               @input="updateReductionInLife"
                               name="period_between_onset_of_symptoms_and_assumed_dod"
                               type="number">
                    </control>
                    <control for="age_at_onset_of_symptoms"
                             label="Claimant's Age at Onset of Symptoms" before-after="years">
                        <input autocomplete="off" v-model.number="ageAtOnsetOfSymptoms"
                               v-field-control
                               required
                               disabled
                               v-two-dp
                               name="age_at_onset_of_symptoms"
                               type="number">
                    </control>
                </div>
            </div>
        </form>
        <step-button :next-valid="isValid()"></step-button>
    </div>
</template>

<script type="text/babel">
    import _ from 'lodash';
    import {mapActions, mapGetters} from 'vuex';
    import moment from 'moment';
    import StepButton from 'components/StepButton.vue';
    import Control from 'components/form/Control.vue';
    import ControlError from 'components/form/control/Error.vue';
    import FormValidator from 'components/form/validator/mixin';
    import {setStepValid} from 'services/routingMethods';
    import DateInput from 'components/form/control/DateInput.vue';
    import LifeExpectancy from 'components/LifeExpectancy.vue';
    import TwoDecimalPlaces from 'directives/TwoDecimalPlaces';

    export default {
        name: 'claim-info',
        mounted() {
            this.updateLifeExpectancy(this.claimant.assumedLifeExpectancy);
            this.updateAssumedLifeExpectancy();
        },
        data() {
            return {
                theDate: moment().format('DD/MM/YYYY')
            }
        },
        computed: {
            ...mapGetters('lost/claimant', [
                'claimant',
                'getClaimantValue',
                'getClaimantDetail',
                'getClaimantInfoValue'
            ]),
            ...mapGetters('lost/claimInfo', [
                'getClaimInfoValue',
            ]),
            innerLifeExpectancy() {
                return _.cloneDeep(this.claimant.lifeExpectancy);
            },
            innerAssumedLifeExpectancy() {
                return _.cloneDeep(this.claimant.assumedLifeExpectancy);
            },
            currentAge() {
                return moment().diff(moment(this.claimant.dateOfBirth, 'DD/MM/YYYY'), 'years');
            },
            claimantReductionInLifeRules() {
                if (this.claimantReductionInLife < 0) {
                    return false;
                }
                return true;
            },
            onsetRules() {
                if (!this.onsetOfSymptoms) {
                    return true;
                }

                let mOnsetOfSymptoms = moment(this.onsetOfSymptoms, 'DD/MM/YYYY');

                if (!mOnsetOfSymptoms.isValid()) {
                    return true;
                }

                return mOnsetOfSymptoms.isAfter(moment(this.claimant.dateOfBirth, 'DD/MM/YYYY'));
            },
            lifeExpectancyRules() {
                return (this.getClaimantValue('lifeExpectancy').dateOfDeath);
            },
            ageAtOnsetOfSymptoms: {
                get() {
                    return this.getClaimantValue('ageAtOnset');
                },
            },
            assumedLifeExpectancyMethod: {
                get() {
                    return this.getClaimantValue('assumedLifeExpectancy').method;
                },
                set(value) {
                    this.innerAssumedLifeExpectancy.method = value;
                    if (value === "assumed_date") {
                        this.dateOfDeath = null
                    }
                }
            },
            periodBetweenOnsetAndAssumedDod: {
                get() {
                    return this.getClaimInfoValue('periodBetweenOnsetAndAssumedDod');
                }
            },
            claimantReductionInLife: {
                get() {
                    return this.getClaimInfoValue('claimantReductionInLife');
                }
            },
            yearsLeft: {
                get() {
                    return this.getClaimantValue('assumedLifeExpectancy').yearsLeft;
                },
                set(value) {
                    this.innerAssumedLifeExpectancy.yearsLeft = value;
                }
            },
            monthsLeft: {
                get() {
                    return this.getClaimantValue('assumedLifeExpectancy').monthsLeft;
                },
                set(value) {
                    this.innerAssumedLifeExpectancy.monthsLeft = value;
                }
            },
            dateOfDeath: {
                get() {
                    return this.getClaimantValue('assumedLifeExpectancy').dateOfDeath;
                },
                set(value) {
                    this.innerAssumedLifeExpectancy.dateOfDeath = value;
                }
            },
            ageAtDeath: {
                get() {
                    return this.getClaimantValue('assumedLifeExpectancy').ageAtDeath;
                },
                set(value) {
                    this.innerAssumedLifeExpectancy.ageAtDeath = value;
                }
            },
            percentageOfNormalLifeExpectancy: {
                get() {
                    return this.getClaimantValue('assumedLifeExpectancy').percentageOfNormalLifeExpectancy;
                },
                set(value) {
                    this.innerAssumedLifeExpectancy.percentageOfNormalLifeExpectancy = value;
                }
            },
            percentageReductionInNormalLifeExpectancy: {
                get() {
                    return this.getClaimantValue('assumedLifeExpectancy').percentageReductionInNormalLifeExpectancy;
                },
                set(value) {
                    this.innerAssumedLifeExpectancy.percentageReductionInNormalLifeExpectancy = value;
                }
            },
            percentageLabel() {
                return this.innerAssumedLifeExpectancy.method === 'percentage_of_normal_life_expectancy' ?
                    'Percentage of Normal Life Expectancy' :
                    'Percentage Reduction in Normal Life Expectancy';
            },
            dateOfBirth: {
                get() {
                    return this.getClaimantValue('dateOfBirth');
                },
            },
            levelOfEducation: {
                get() {
                    return this.getClaimantValue('levelOfEducation');
                },
                set(value) {
                    this.updateClaimantValue({key: 'levelOfEducation', value});
                }
            },
            disabledAtAccident: {
                get() {
                    return this.getClaimantValue('disabledAtAccident');
                },
                set(value) {
                    this.updateClaimantValue({key: 'disabledAtAccident', value});
                }
            },
            onsetOfSymptoms: {
                get() {
                    return this.getClaimantValue('onsetOfSymptoms');
                },
                set(value) {
                    this.updateClaimantValue({key: 'onsetOfSymptoms', value});
                    this.updateOnsetAndAssumedDod();
                }
            },
            serviceOfProceedings: {
                get() {
                    return this.getClaimInfoValue('serviceOfProceedings');
                },
            },
        },
        directives: {
            'two-dp': TwoDecimalPlaces
        },
        mixins: [
            FormValidator
        ],
        components: {
            StepButton,
            Control,
            ControlError,
            DateInput,
            LifeExpectancy
        },
        watch: {
            ['$form.$valid']() {
                this.updateValid()
            },
            ['onsetRules']() {
                this.updateValid()
            },
            ['claimantReductionInLifeRules']() {
                this.updateValid()
            }
        },
        methods: {
            ...mapActions('lost/claimant', [
                'updateLifeExpectancy',
                'updateClaimantValue',
                'updateClaimantDetail',
                'updateClaimantAssumedLifeExpectancy',
                'updateReductionInLife',
                'updateOnsetAndAssumedDod'
            ]),
            ...mapActions('lost/claimInfo', [
                'updateClaimInfoValue',
            ]),
            updateAssumedLifeExpectancy() {
                let assumedLifeExpectancy = this.innerAssumedLifeExpectancy;
                this.updateClaimantAssumedLifeExpectancy({assumedLifeExpectancy});
            },
            updateLife(lifeExpectancy) {
                this.updateLifeExpectancy({lifeExpectancy})
            },
            updateValid() {
                setStepValid(this.isValid());
            },
            isValid() {
                return this.$valid()
                    && this.onsetRules
                    && this.claimantReductionInLifeRules;
            },
        }
    }
</script>



// WEBPACK FOOTER //
// ClaimInfo.vue?a98b459a